import React, { useEffect, useRef, useState } from 'react';
import { Camera, UserRound } from 'lucide-react';
import { collection, doc, getDocs, limit, query, updateDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../AuthContext';
import { artistFields, validateArtistFields } from '../artistProfile';
import ArtistProfileFields from './ArtistProfileFields';
import LocationAutocomplete, { locationFromProfile } from './LocationAutocomplete';
import { hasCoordinates } from '../locationFeed';

export default function ArtistProfileForm({ onSaved, onboarding = false }) {
  const { user, profile } = useAuth();
  const inputRef = useRef();
  const [username, setUsername] = useState(profile?.username || '');
  const [bio, setBio] = useState(profile?.bio || '');
  const [location, setLocation] = useState(() => locationFromProfile(profile || {}));
  const [artCategories, setArtCategories] = useState(() => artistFields(profile).artCategories);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(profile?.photoURL || user?.photoURL || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!file) return undefined;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const choose = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (!selected.type.startsWith('image/')) { setError('Choose an image for your profile photo.'); return; }
    setError('');
    setFile(selected);
  };

  const save = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    const name = username.trim();
    if (name.length < 4 || name.length > 30 || !/^[a-zA-Z0-9._]+$/.test(name) || !/[a-zA-Z]/.test(name)) return setError('Use 4-30 characters: letters, numbers, dots, or underscores. Include a letter.');
    if (!bio.trim()) return setError('Tell the community a little about yourself.');
    if (bio.trim().length > 140) return setError('Keep your bio under 140 characters.');
    if (!location?.area || !location?.city || !location?.country || !hasCoordinates(location)) return setError('Select a location from the search suggestions.');
    const artistError = validateArtistFields(artCategories, []);
    if (artistError) return setError(artistError);
    setBusy(true);
    try {
      const usernameLower = name.toLowerCase();
      const matches = await getDocs(query(collection(db, 'users'), where('usernameLower', '==', usernameLower), limit(1)));
      if (!matches.empty && matches.docs[0].id !== user.uid) throw new Error('That username is already in use.');
      let photoURL = preview.startsWith('blob:') ? '' : preview;
      if (file) {
        const imageRef = ref(storage, `profileImages/${user.uid}.jpg`);
        await uploadBytes(imageRef, file, { contentType: file.type });
        photoURL = await getDownloadURL(imageRef);
      }
      await updateDoc(doc(db, 'users', user.uid), {
        username: name, usernameLower, bio: bio.trim(), photoURL,
        area: location.area, city: location.city, country: location.country,
        lat: location.lat, lng: location.lng, locationLabel: location.label,
        locationPlaceId: location.placeId, artCategories,
        locationProvider: location.locationProvider || profile?.locationProvider || '',
        professions: artCategories, musicianSubcategories: [],
        area_member: true, artist_area: true, updatedAt: new Date().toISOString(),
      });
      onSaved();
    } catch (err) {
      setError(err.message || 'Unable to save your profile.');
    } finally { setBusy(false); }
  };

  return <form className={onboarding ? 'form-card profile-form' : 'edit-form'} onSubmit={save}>
    <fieldset className="profile-form-controls" disabled={busy}>
      <button type="button" className="photo-picker" onClick={() => inputRef.current?.click()}>
        {preview ? <img src={preview} alt="Profile preview" onError={() => setPreview('')} /> : <UserRound />}
        <span><Camera size={14} /></span>
      </button>
      <p className="profile-photo-hint muted">Profile photo (optional)</p>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={choose} />
      <label>USERNAME *<input value={username} onChange={(event) => setUsername(event.target.value)} maxLength={30} placeholder="your.name" required /></label>
      <label><span className="label-row">BIO * <small>{bio.length}/140</small></span><textarea value={bio} onChange={(event) => setBio(event.target.value)} maxLength={140} placeholder="About you" required /></label>
      <LocationAutocomplete value={location} onChange={setLocation} required />
      <ArtistProfileFields {...{ artCategories, setArtCategories }} />
      {error && <p className="form-error" role="alert">{error}</p>}
      <button className="primary-button" disabled={busy}>{busy ? 'Saving...' : onboarding ? 'Enter Area' : 'Save changes'}</button>
    </fieldset>
  </form>;
}
